import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, HelpCircle, ArrowRight, Phone, BookOpen } from 'lucide-react';

const LOGO_URL = "/inasw-logo-2026.jpg";
const SUPPORT_EMAIL = "support@ineedasocialworker.com";
const PHONE_NUMBER = "254-624-3308";
const API_URL = process.env.REACT_APP_BACKEND_URL;
const AMAZON_BOOK_LINK = "https://a.co/d/041VSz1V";
const FACEBOOK_URL = "https://www.facebook.com/share/1CB7tPXV6d/?mibextid=wwXIfr";
const TIKTOK_URL = "https://www.tiktok.com/@ineedasocialworker";
const LINKEDIN_URL = "https://www.linkedin.com/in/ineedasocialworker";
const INSTAGRAM_URL = "https://www.instagram.com/needasocialworker/";
const YOUTUBE_URL = "https://www.youtube.com/@needsocialworker";
const X_URL = "https://x.com/ineedasw";

// Track analytics
const trackEmailClick = () => {
  try {
    axios.post(`${API_URL}/api/analytics/contact`, {
      event_type: 'email_click',
      source: 'footer'
    });
  } catch (error) {
    // Silent fail
  }
};

const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-primary to-[#0F2E24] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Top Section - Brand + Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="INASW Logo" className="h-12 w-12 rounded-full object-cover ring-2 ring-secondary/50 shadow-sm" />
              <div>
                <span className="font-serif text-xl font-semibold text-white">
                  I Need A Social Worker
                </span>
              </div>
            </Link>
            <p className="text-gray-300 text-sm mb-4">
              Connecting licensed social workers with meaningful career opportunities.
            </p>
            <p className="text-secondary font-medium text-sm mb-4">
              Powered by Diversified Social Work Solutions
            </p>
            
            {/* Social Media Links */}
            <div className="flex items-center gap-3">
              <a 
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#1877F2] flex items-center justify-center transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook-link"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-black flex items-center justify-center transition-colors"
                aria-label="TikTok"
                data-testid="footer-tiktok-link"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#0A66C2] flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
                data-testid="footer-linkedin-link"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#E4405F] flex items-center justify-center transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#FF0000] flex items-center justify-center transition-colors"
                aria-label="YouTube"
                data-testid="footer-youtube-link"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-black flex items-center justify-center transition-colors"
                aria-label="X"
                data-testid="footer-x-link"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links — real site pages only */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/jobs" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-browse-jobs">
                  Browse Jobs
                </Link>
              </li>
              <li>
                <Link to="/community/resources" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-resources-directory">
                  Community Resources
                </Link>
              </li>
              <li>
                <Link to="/community/ask-a-social-worker" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-ask-community">
                  Ask a Social Worker
                </Link>
              </li>
              <li>
                <Link to="/find-a-supervisor" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-find-supervisor">
                  Find LCSW-S Supervisor
                </Link>
              </li>
              <li>
                <Link to="/internships" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-internships">
                  Internships
                </Link>
              </li>
              <li>
                <Link to="/internship-portal" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-internship-portal">
                  Internship Portal
                </Link>
              </li>
              <li>
                <Link to="/license-renewal-center" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-license-center">
                  License &amp; Renewal Center
                </Link>
              </li>
              <li>
                <Link to="/advance-directives" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-advance-directives">
                  Advance Directives Database
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-about">
                  About I Need A Social Worker
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-contact">
                  Contact
                </Link>
              </li>
            </ul>

            {/* Job Categories */}
            <h4 className="font-serif text-lg font-semibold mt-6 mb-3 text-white">Job Categories</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/jobs?license=LBSW" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  LBSW Jobs
                </Link>
              </li>
              <li>
                <Link to="/jobs?license=LMSW" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  LMSW Jobs
                </Link>
              </li>
              <li>
                <Link to="/jobs?license=LCSW" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  LCSW Jobs
                </Link>
              </li>
              <li>
                <Link to="/hospital-social-worker-jobs" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  Hospital Social Worker
                </Link>
              </li>
              <li>
                <Link to="/school-social-worker-jobs" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  School Social Worker
                </Link>
              </li>
              <li>
                <Link to="/hospice-social-worker-jobs" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  Hospice Social Worker
                </Link>
              </li>
              <li>
                <Link to="/clinical-social-worker-jobs" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  Clinical Social Worker
                </Link>
              </li>
              <li>
                <Link to="/behavioral-health-social-worker-jobs" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-behavioral-health">
                  Behavioral Health Social Worker
                </Link>
              </li>
              <li>
                <Link to="/forensic-social-worker-jobs" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-forensic">
                  Forensic Social Work Jobs
                </Link>
              </li>
            </ul>
          </div>

          {/* For Employers */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4 text-white">For Employers</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/register?type=employer" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  Post a Job
                </Link>
              </li>
              <li>
                <Link to="/become-a-supervisor" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-become-supervisor">
                  Become a Listed Supervisor
                </Link>
              </li>
              <li>
                <Link to="/employer-faq" className="text-gray-300 hover:text-secondary transition-colors text-sm">
                  Employer FAQ
                </Link>
              </li>
            </ul>

            {/* Account */}
            <h4 className="font-serif text-lg font-semibold mt-6 mb-3 text-white">Account</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/login" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-login">
                  Sign In
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-gray-300 hover:text-secondary transition-colors text-sm" data-testid="footer-create-account">
                  Create Account
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Resources */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4 text-white">Contact & Resources</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Phone className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                <a 
                  href={`tel:${PHONE_NUMBER}`}
                  className="hover:text-secondary"
                  data-testid="footer-phone"
                >
                  {PHONE_NUMBER}
                </a>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Mail className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                <a 
                  href={`mailto:${SUPPORT_EMAIL}`}
                  onClick={trackEmailClick}
                  className="hover:text-secondary break-words text-left"
                  data-testid="footer-support-email"
                >
                  {SUPPORT_EMAIL}
                </a>
              </li>
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <BookOpen className="w-4 h-4 text-secondary flex-shrink-0" />
                <Link to="/resources" className="hover:text-secondary transition-colors">
                  Resources & Books
                </Link>
              </li>
              <li className="flex items-center gap-2 mt-4">
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 bg-secondary/20 hover:bg-secondary/30 text-secondary px-4 py-2 rounded-lg text-sm transition-colors"
                  data-testid="footer-contact-link"
                >
                  <HelpCircle className="w-4 h-4" />
                  Contact Us
                </Link>
              </li>
            </ul>
            
            {/* Book Link */}
            <div className="mt-6 p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-white font-medium mb-2">Travel Log for Social Workers</p>
              <a 
                href={AMAZON_BOOK_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-secondary hover:text-secondary/80 text-sm font-medium"
              >
                Shop on Amazon
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Disclaimer Section */}
        <div className="border-t border-white/10 pt-8">
          <div className="bg-white/5 rounded-lg p-4 mb-6 max-w-4xl mx-auto">
            <p className="text-sm text-gray-300 text-center leading-relaxed">
              <strong className="text-white">Important:</strong> Results are not guaranteed. All fees are non-refundable 
              as your job may not be in demand or may not meet applicant requirements. 
              Use of this site constitutes acceptance of our{' '}
              <Link to="/terms" className="text-secondary hover:underline">Terms & Policies</Link>.
            </p>
          </div>
          
          {/* Copyright - Small & Centered */}
          <p className="text-gray-400 text-xs text-center">
            © {new Date().getFullYear()} I Need A Social Worker. All rights reserved. | 
            <Link to="/privacy" className="hover:text-secondary ml-1">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
